/**
 * Traduz a escolha do `SeletorUnidadeDados` nos parâmetros do pedido.
 *
 * Vive aqui, e não em cada ecrã, porque são vários ecrãs a fazer a mesma
 * pergunta e uma divergência entre eles seria invisível até alguém reparar que
 * uma lista tem mais linhas do que outra. Gémeo do da v2 — manter iguais.
 *
 * Três casos, e o do meio é o que não é óbvio:
 *
 *   TODAS          → `todasUnidades: true`. O servidor devolve as unidades do
 *                    utilizador, incluindo registos ainda sem unidade.
 *
 *   a da SESSÃO    → NÃO se manda nada. O servidor usa a unidade da sessão e
 *                    trata-a como omissão, o que TRAZ TAMBÉM os registos ainda
 *                    sem unidade. É o que impede estes ecrãs de aparecerem
 *                    vazios enquanto a migração não correr em produção.
 *
 *   outra unidade  → `estabelecimento: <id>`. Escolha deliberada, estrita: só
 *                    o que está carimbado com ela. Se os registos sem unidade
 *                    viessem em qualquer escolha, apareciam nas duas e o
 *                    dinheiro contava a dobrar.
 *
 * @param {string} valor  o do seletor: um id, ou "todas"
 * @param {object} auth   o AuthContext
 * @returns {object} pares a acrescentar ao corpo do pedido
 */
export const paramsUnidadeDados = (valor, auth) => {
  if (valor === "todas") return { todasUnidades: true };

  const daSessao = auth?.estabelecimento?._id;
  if (!valor || String(valor) === String(daSessao)) return {};

  return { estabelecimento: valor };
};

/**
 * A mesma escolha, mas em query string, para os ecrãs que pedem por GET.
 *
 * Alguns pedidos são `PATCH` com `FormData` e outros `GET` com a unidade no
 * URL; a decisão de que unidade é tem de ser a mesma nos dois, senão a lista de
 * atendimento e a agenda do mesmo dia mostram coisas diferentes. Existia como
 * `qsUnidade` local em dois ficheiros da agenda e faltava nos três da lista de
 * atendimento — foi assim que os compromissos ficaram sem filtro.
 *
 * Devolve "" quando é a unidade da sessão: não se manda nada e o servidor
 * decide, com a tolerância de sempre.
 *
 * @param {string} valor        o do seletor: um id, ou "todas"
 * @param {object} auth         o AuthContext
 * @param {boolean} jaTemQuery  o URL já tem "?" — junta-se com "&"
 */
export const queryUnidadeDados = (valor, auth, jaTemQuery = false) => {
  const params = new URLSearchParams(paramsUnidadeDados(valor, auth)).toString();
  if (!params) return "";
  return (jaTemQuery ? "&" : "?") + params;
};

/**
 * Pode este registo ser alterado a partir da unidade onde estou?
 *
 * Espelha a guarda `garantirMesmaUnidade` do servidor, incluindo as duas
 * tolerâncias — e tem de as espelhar exatamente, senão a interface desativa
 * botões que o servidor aceitaria, ou deixa ativos os que ele recusa:
 *
 *   - SEM UNIDADE na sessão → pode. Clínicas de unidade única e sessões
 *     anteriores ao multi-estabelecimento.
 *   - SEM UNIDADE no registo → pode. Enquanto a migração não correr, nenhum
 *     registo tem unidade.
 *
 * Isto é conveniência, não segurança: quem contornar a interface é recusado
 * pelo servidor à mesma.
 *
 * @param {object|string|null} unidadeDoRegisto  `registo.estabelecimento`
 * @param {object} auth                          o AuthContext
 */
export const podeAlterarRegisto = (unidadeDoRegisto, auth) => {
  const daSessao = auth?.estabelecimento?._id;
  if (!daSessao) return true;

  const doRegisto = unidadeDoRegisto?._id ?? unidadeDoRegisto;
  if (!doRegisto) return true;

  return String(doRegisto) === String(daSessao);
};

/**
 * Texto a mostrar quando a ação está bloqueada. Nomeia a unidade do registo
 * para a pessoa perceber para onde tem de mudar.
 *
 * O verbo é parametrizável porque «alterar» não serve em todo o lado: quem
 * carrega em «Executar» e lê «mude de unidade para alterar» tem de traduzir
 * mentalmente. Dizer o mesmo verbo do botão que a pessoa acabou de carregar
 * poupa-lhe esse passo. O valor por omissão mantém o texto que os outros ecrãs
 * já mostram.
 *
 * @param {object|null} unidadeDoRegisto  `registo.estabelecimento`
 * @param {string} [acao]  o verbo, no infinitivo: "executar", "receber"…
 */
export const motivoBloqueioUnidade = (unidadeDoRegisto, acao = "alterar") => {
  const nome =
    unidadeDoRegisto?.nome || unidadeDoRegisto?.codigo || "outra unidade";
  return `Pertence a ${nome}. Mude de unidade para ${acao}.`;
};

/**
 * O NOME da unidade escolhida num seletor, para imprimir num cabeçalho.
 *
 * O seletor guarda só o id — ou `"todas"` — e quem imprime precisa do nome.
 * Em vez de o ir buscar ao servidor outra vez, lê-se das linhas que já estão
 * no ecrã: elas trazem o `estabelecimento` populado.
 *
 * Existe porque as listas do dia NÃO se guardam: uma lista de atendimentos é
 * impressa e deitada fora, e por isso não leva pastilha por linha — mas quem a
 * tem na mão precisa de saber de que unidade ela é.
 *
 * @param {string|null} valor   o do seletor: um id, "todas", ou null
 * @param {object} auth         o AuthContext
 * @param {Array} [linhas]      registos com `estabelecimento` populado
 * @returns {string} o nome, ou "" quando não há unidade nenhuma a nomear
 */
export const nomeUnidadeEscolhida = (valor, auth, linhas = []) => {
  if (valor === "todas") return "Todas as unidades";

  const daSessao = auth?.estabelecimento;
  if (!valor || String(valor) === String(daSessao?._id)) {
    return daSessao?.nome || daSessao?.codigo || "";
  }

  const linha = (linhas || []).find(
    (l) => String(l?.estabelecimento?._id) === String(valor),
  );
  return linha?.estabelecimento?.nome || linha?.estabelecimento?.codigo || "";
};

/**
 * Pode operar-se sobre este DOCUMENTO ELETRÓNICO a partir da unidade atual?
 *
 * É uma pergunta diferente da `podeAlterarRegisto`, e a diferença é fiscal.
 * Cada unidade emite sob o seu LED e a sua série. Um documento que se refere a
 * outro — o recibo de uma fatura, a nota de crédito que a anula — tem de sair
 * pelo MESMO LED do original. Emitir o recibo de uma fatura de Assomada sob o
 * LED da Praia produz um documento que a e-fatura não liga ao original.
 *
 * Por isso esta regra vale mesmo para quem tem acesso às duas unidades: não é
 * uma questão de permissão, é de identidade do emissor.
 *
 * Espelha `garantirMesmaUnidadeDoDocumento` no servidor, incluindo as duas
 * tolerâncias — sem unidade na sessão e sem unidade no documento não bloqueia.
 *
 * @param {object|null} documento  o DFE (com `estabelecimento` populado ou id)
 * @param {object} auth            o AuthContext
 */
export const podeMexerNoDocumento = (documento, auth) => {
  if (!documento) return true;

  const daSessao = auth?.estabelecimento?._id;
  if (!daSessao) return true;

  const doDocumento =
    documento.estabelecimento?._id ?? documento.estabelecimento;
  if (!doDocumento) return true;

  return String(doDocumento) === String(daSessao);
};

/**
 * Porquê que a operação sobre o documento está bloqueada. Explica a razão, não
 * só a proibição — quem lê tem de perceber que não é avaria.
 */
export const motivoBloqueioDocumento = (documento) => {
  const unidade = documento?.estabelecimento;
  const nome = unidade?.nome || unidade?.codigo || "outra unidade";
  const tipo = documento?.tipoDocumento || "Documento";

  return (
    `${tipo} emitido por ${nome}, sob o LED dessa unidade. ` +
    `Recibos e notas de crédito têm de sair pelo mesmo LED, ` +
    `por isso esta operação tem de ser feita a partir de ${nome}.`
  );
};
